// find-the-bug-chrome — the isolated "find-the-bug" surface (find-the-bug-activity, Trello
// C902VeMO). Host chrome AROUND the unchanged runner.
//
//   wire(ctx) — ctx = { core, payload, trinketId, clientsideMode, isOwner, editorApi, doc }
//
// The flow, in three stages:
//   1. TEST — the buggy program sits READ-ONLY in the editor. The student builds a test table
//      (input(s), a row type, a predicted output or "expect an error", an optional why), and
//      presses Run tests. Every row runs TWICE, one runner call per row, each bounded to 2 s:
//      the code under test in the VISIBLE runner, and the hidden `reference` in a second,
//      off-screen runner (`hidden-runner.js`). A row whose prediction the reference meets but
//      the code under test does not is evidence of the bug.
//   2. FIX — the first such row UNLOCKS the editor (Ace `setReadOnly(false)`, the
//      assignment-chrome schedule-lock precedent). The student fixes the same working copy,
//      which autosaves to its Draft, and re-runs. EVERY cell stays editable: a mispredicted row
//      can be corrected, but a row only passes when BOTH the code under test and the reference
//      meet its prediction, so retyping a prediction to match broken code never passes.
//   3. EXPLAIN — once a fresh run passes every row, the explanation can be submitted. That is
//      the ONLY action that completes the activity; its response carries the teacher's model
//      explanation, shown immediately for self-checking.
//
// Every rule lives in the pure `find-the-bug-grading.js`; this file owns the DOM and the runner
// calls. The reference's own output is never rendered, never announced and never POSTed.
// NO runner-contract change; editor-core untouched.

import ace from 'ace-builds/src-noconflict/ace';
import { createHiddenRunner, runWithTimeout, waitForReady, DEFAULT_CALL_TIMEOUT_MS } from './hidden-runner.js';
import {
  ROW_TYPES, RESULT, QUOTE_HINT,
  buildParseProgram, readParseOutput, rowInputs,
  observeFunctionResult, observeConsoleRun, gradeRow, summarise, stickyBugFound,
  canSubmitExplanation, formatObserved, coverage, resultLabel
} from './find-the-bug-grading.js';
import { mountPreviewStrip } from './preview-strip.js';
import { inlineMarkdown } from './inline-markdown.js';
import { offerClose, offerReturn } from './close-prompt.js';
import { registerSaveHandler } from './editor-shortcuts.js';

var AUTOSAVE_DEBOUNCE_MS = 1500;

// one-tab-same-tab-launches (Trello bD1oe35m): the wired page's flush, or null before `wire`
// reaches its autosave — challenge-chrome.js's contract exactly.
var activeFlush = null;

// Cancel the autosave debounce and save the live files NOW. Resolves the draft endpoint's reply
// (`{ success: true }` when it saved), rejects on a network/HTTP failure, and resolves `null` when
// nothing autosaves — no surface wired, or the bug not yet found (the code is read-only and never
// saved until then). The activity top bar's Back awaits it.
export function flushPendingSave() {
  return activeFlush ? activeFlush() : Promise.resolve(null);
}

// EXPLICIT save state (see `assignment-chrome.js` for the full rule) — `pending` (debounce
// scheduled), `inflight` (save requests outstanding), `lastFailed` (the last save to finish
// rejected or resolved `success !== true`, cleared by the next success). Before the bug is found
// no save is ever scheduled, so nothing reports pending.
var saveState = { wired: false, pending: false, inflight: 0, lastFailed: false };

export function hasPendingSave() {
  if (!saveState.wired) { return false; }
  return saveState.pending || saveState.inflight > 0 || saveState.lastFailed;
}

function trackSave(promise) {
  saveState.inflight += 1;
  return Promise.resolve(promise).then(function (res) {
    saveState.inflight = Math.max(0, saveState.inflight - 1);
    saveState.lastFailed = !(res && res.success === true);
    return res;
  }, function (err) {
    saveState.inflight = Math.max(0, saveState.inflight - 1);
    saveState.lastFailed = true;
    throw err;
  });
}

/** Test-only reset so module state does not leak between specs. */
export function __resetFindTheBugSaveState() {
  activeFlush = null;
  saveState = { wired: false, pending: false, inflight: 0, lastFailed: false };
}
var PARSE_TIMEOUT_MS = 8000;
var TYPE_LABELS = { expected: 'Expected', boundary: 'Boundary', invalid: 'Invalid' };
var RESULT_ICONS = {
  pass: '<path d="M20 6 9 17l-5-5"/>',
  fail: '<path d="M18 6 6 18"/><path d="M6 6l12 12"/>',
  mispredict: '<circle cx="12" cy="12" r="9"/><path d="M12 8v5"/><path d="M12 16h.01"/>',
  timeout: '<circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/>',
  'bad-literal': '<circle cx="12" cy="12" r="9"/><path d="M12 8v5"/><path d="M12 16h.01"/>'
};

function readConfig(doc) {
  var el = doc.getElementById('find-the-bug-config');
  if (!el) { return null; }
  try {
    var cfg = JSON.parse(el.textContent || '{}');
    return (cfg && typeof cfg === 'object') ? cfg : null;
  } catch (e) { return null; }
}

function svg(doc, paths, size) {
  var s = doc.createElementNS('http://www.w3.org/2000/svg', 'svg');
  s.setAttribute('viewBox', '0 0 24 24');
  s.setAttribute('width', String(size || 16));
  s.setAttribute('height', String(size || 16));
  s.setAttribute('fill', 'none');
  s.setAttribute('stroke', 'currentColor');
  s.setAttribute('stroke-width', '2');
  s.setAttribute('stroke-linecap', 'round');
  s.setAttribute('stroke-linejoin', 'round');
  s.setAttribute('aria-hidden', 'true');
  s.innerHTML = paths;
  return s;
}

export function wire(ctx) {
  var doc  = ctx.doc;
  var core = ctx.core;
  var api  = ctx.editorApi;
  var cfg  = readConfig(doc);
  if (!cfg) { return; }

  var previewMode = cfg.preview === true;
  // Launched SAME-TAB in preview (guided-make precedent), so the strip navigates back.
  // one-tab-same-tab-launches: the activity top bar's preview chip and "Back to class" replace
  // the strip, so it is mounted only when the bar is absent (card 2's guard).
  if (!doc.getElementById('surface-topbar-back')) {
    mountPreviewStrip(doc, {
      active: previewMode,
      exit: 'navigate',
      exitUrl: cfg.returnUrl || '',
      reloadFallback: true
    });
  }

  var panel = doc.getElementById('find-the-bug-panel');
  if (!panel) { return; }

  var target   = cfg.target === 'stdin' ? 'stdin' : 'function';
  var compare  = target === 'stdin' ? 'stdout' : (cfg.compare === 'stdout' ? 'stdout' : 'return');
  var params   = Array.isArray(cfg.params) ? cfg.params.filter(function (p) { return typeof p === 'string'; }) : [];
  var inputCount = target === 'stdin'
    ? Math.max(1, Math.min(10, Number(cfg.inputCount) || 1))
    : params.length;
  var maxRows  = Math.max(1, Math.min(30, Number(cfg.maxRows) || 30));
  var canManage = !!cfg.canManage;

  // ── state ────────────────────────────────────────────────────────────────────
  var resumed = cfg.resumed || null;
  var rows = [];
  var bugFound = !!(resumed && resumed.bugFound);
  var allPass = !!(resumed && resumed.allPass);
  var runFresh = false;           // does allPass describe the table + code as they are NOW?
  var correct = !!(resumed && resumed.correct);
  var explanation = (resumed && typeof resumed.explanation === 'string') ? resumed.explanation : '';
  var explainModel = (typeof cfg.explainModel === 'string') ? cfg.explainModel : '';
  var running = false;
  var submitting = false;
  var hidden = null;              // the off-screen reference runner, built once, kept warm
  var hiddenFailed = false;
  // The VISIBLE runner's readiness. wire() runs synchronously right after core.load(), before
  // any runner message can arrive, so this listener always sees the first `ready`. A Run tests
  // click before then waits for it (ensureVisible) instead of posting into a runner that is
  // not up — which would blow the 2 s budget and force a needless Pyodide restart.
  var visibleReady = false;
  core.on('ready', function () { visibleReady = true; });

  function blankRow() {
    var inputs = [];
    for (var i = 0; i < inputCount; i++) { inputs.push(''); }
    return { inputs: inputs, type: 'expected', expectError: false, expected: '', why: '', actual: '', result: '' };
  }
  if (resumed && Array.isArray(resumed.rows) && resumed.rows.length) {
    rows = resumed.rows.slice(0, maxRows).map(function (r) {
      return {
        inputs: rowInputs(r, inputCount),
        type: ROW_TYPES.indexOf(r && r.type) !== -1 ? r.type : 'expected',
        expectError: !!(r && r.expectError),
        expected: (r && typeof r.expected === 'string') ? r.expected : '',
        why: (r && typeof r.why === 'string') ? r.why : '',
        actual: (r && typeof r.actual === 'string') ? r.actual : '',
        result: (r && typeof r.result === 'string') ? r.result : ''
      };
    });
  } else {
    rows = [blankRow()];
  }

  // ── DOM helpers ──────────────────────────────────────────────────────────────
  function el(tag, cls, text) {
    var n = doc.createElement(tag);
    if (cls) { n.className = cls; }
    if (text != null) { n.textContent = text; }
    return n;
  }
  function button(cls, text) {
    var b = el('button', 'ftb-btn' + (cls ? ' ' + cls : ''), text);
    b.type = 'button';
    return b;
  }
  var uid = 0;
  function nextId(prefix) { uid += 1; return 'ftb-' + prefix + '-' + uid; }

  // ── header + stages ───────────────────────────────────────────────────────────
  var head = el('div', 'ftb-head');
  var heading = el('h2', 'ftb-heading');
  heading.appendChild(svg(doc, '<circle cx="11" cy="11" r="7"/><path d="m21 21-4.3-4.3"/>', 20));
  heading.appendChild(doc.createTextNode('Find the bug'));
  head.appendChild(heading);
  var stages = el('ol', 'ftb-stages');
  stages.setAttribute('aria-label', 'Your progress');
  var stageEls = ['Test it', 'Fix it', 'Explain it'].map(function (label) {
    var li = el('li', 'ftb-stage', label);
    stages.appendChild(li);
    return li;
  });
  head.appendChild(stages);
  var lockNote = el('p', 'ftb-note');
  head.appendChild(lockNote);
  panel.appendChild(head);

  if (canManage && !previewMode) {
    panel.appendChild(el('div', 'ftb-preview-note',
      'You are a teacher, so your runs here are not recorded as a student\'s.'));
  }

  // ── the spec ──────────────────────────────────────────────────────────────────
  var specSection = el('section', 'ftb-section');
  var specLabelId = nextId('spec');
  var specLabel = el('h3', 'ftb-label', 'What the program should do');
  specLabel.id = specLabelId;
  specSection.setAttribute('aria-labelledby', specLabelId);
  specSection.appendChild(specLabel);
  // code-a-game-browser-fixes (B): authored prose, rendered escape-first (inline-markdown.js).
  var specText = el('p', 'ftb-spec');
  specText.innerHTML = inlineMarkdown(cfg.spec || 'Your teacher has not described this program yet.');
  specSection.appendChild(specText);
  var howTo = target === 'stdin'
    ? 'Each test types ' + (inputCount === 1 ? 'one value' : inputCount + ' values') + ' into the program, one per input(). Predict what it prints.'
    : 'Each test calls ' + (cfg.functionName || 'the function') + '(' + params.join(', ') + ')'
      + (compare === 'return' ? ' and checks the value it returns.' : ' and checks what it prints.');
  specSection.appendChild(el('p', 'ftb-note', howTo + ' Type values the way Python writes them: 5, 2.5, "hello", True, None.'));
  panel.appendChild(specSection);

  // ── try one input (ungraded) ──────────────────────────────────────────────────
  var tryBox = el('details', 'ftb-try');
  var trySummary = el('summary', null, 'Try one input first');
  tryBox.appendChild(trySummary);
  var tryBody = el('div', 'ftb-try-body');
  tryBody.appendChild(el('p', 'ftb-note', 'Run the program once with any input to see what it does. Nothing here is marked or saved.'));
  var tryFields = el('div', 'ftb-try-fields');
  var tryInputs = [];
  for (var ti = 0; ti < inputCount; ti++) {
    var f = el('label', 'ftb-field');
    f.appendChild(doc.createTextNode(inputLabel(ti)));
    var inp = el('input', 'ftb-input');
    inp.type = 'text';
    inp.autocomplete = 'off';
    inp.spellcheck = false;
    f.appendChild(inp);
    tryFields.appendChild(f);
    tryInputs.push(inp);
  }
  if (inputCount) { tryBody.appendChild(tryFields); }
  var tryActions = el('div', 'ftb-actions');
  var tryBtn = button('', 'Run once');
  tryActions.appendChild(tryBtn);
  tryBody.appendChild(tryActions);
  var tryOut = el('pre', 'ftb-out');
  tryOut.hidden = true;
  tryOut.setAttribute('aria-live', 'polite');
  tryBody.appendChild(tryOut);
  tryBox.appendChild(tryBody);
  panel.appendChild(tryBox);

  function inputLabel(i) {
    if (target === 'stdin') { return inputCount === 1 ? 'Input' : 'Input ' + (i + 1); }
    return params[i] || ('Argument ' + (i + 1));
  }

  // ── the test table ────────────────────────────────────────────────────────────
  var tableSection = el('section', 'ftb-section');
  var tableLabelId = nextId('table');
  var tableLabel = el('h3', 'ftb-label', 'Your tests');
  tableLabel.id = tableLabelId;
  tableSection.appendChild(tableLabel);
  var tableWrap = el('div', 'ftb-table-wrap');
  tableWrap.setAttribute('role', 'region');
  tableWrap.setAttribute('aria-labelledby', tableLabelId);
  tableWrap.tabIndex = 0;
  var table = el('table', 'ftb-table');
  var thead = el('thead');
  var headRow = el('tr');
  var expectedHeader = compare === 'return' ? 'Should return' : 'Should print';
  ['#', target === 'stdin' ? 'Input' : 'Arguments', 'Type', expectedHeader, 'Actual', 'Result', 'Why this test?', '']
    .forEach(function (h, i) {
      var th = el('th', null, h);
      th.scope = 'col';
      if (i === 7) { th.appendChild(el('span', 'ftb-sr', 'Row tools')); }
      headRow.appendChild(th);
    });
  thead.appendChild(headRow);
  table.appendChild(thead);
  var tbody = el('tbody');
  table.appendChild(tbody);
  tableWrap.appendChild(table);
  tableSection.appendChild(tableWrap);

  var tableActions = el('div', 'ftb-actions');
  var addBtn = button('', 'Add a test');
  var runBtn = button('ftb-btn-run', 'Run tests');
  tableActions.appendChild(addBtn);
  tableActions.appendChild(runBtn);
  tableSection.appendChild(tableActions);
  var status = el('p', 'ftb-status');
  status.setAttribute('role', 'status');
  tableSection.appendChild(status);
  var errorEl = el('p', 'ftb-error');
  errorEl.hidden = true;
  errorEl.setAttribute('role', 'alert');
  tableSection.appendChild(errorEl);
  panel.appendChild(tableSection);

  // ── advisory coverage ─────────────────────────────────────────────────────────
  var coverSection = el('section', 'ftb-section');
  coverSection.appendChild(el('h3', 'ftb-label', 'Test coverage'));
  var coverList = el('ul', 'ftb-coverage');
  coverSection.appendChild(coverList);
  coverSection.appendChild(el('p', 'ftb-note', 'Good tests mix expected inputs, values right on a boundary, and invalid inputs. This is advice — it never stops you.'));
  panel.appendChild(coverSection);

  // ── bug found ─────────────────────────────────────────────────────────────────
  var foundCallout = el('div', 'ftb-callout');
  foundCallout.hidden = true;
  panel.appendChild(foundCallout);

  // ── explain ───────────────────────────────────────────────────────────────────
  var explainSection = el('section', 'ftb-section');
  var explainId = nextId('explain');
  var explainLabel = el('label', 'ftb-label', cfg.explainPrompt || 'What did you change, and how did your tests prove the bug was there?');
  explainLabel.setAttribute('for', explainId);
  explainSection.appendChild(explainLabel);
  var explainBox = el('textarea', 'ftb-textarea');
  explainBox.id = explainId;
  explainBox.maxLength = 5000;
  explainBox.value = explanation;
  explainSection.appendChild(explainBox);
  var explainHint = el('p', 'ftb-note');
  explainHint.id = nextId('explain-hint');
  explainSection.appendChild(explainHint);
  var explainActions = el('div', 'ftb-actions');
  var explainBtn = button('ftb-btn-primary', 'Submit my explanation');
  explainBtn.setAttribute('aria-describedby', explainHint.id);
  explainActions.appendChild(explainBtn);
  explainSection.appendChild(explainActions);
  var explainError = el('p', 'ftb-error');
  explainError.hidden = true;
  explainError.setAttribute('role', 'alert');
  explainSection.appendChild(explainError);
  panel.appendChild(explainSection);

  var doneCallout = el('div', 'ftb-callout');
  doneCallout.hidden = true;
  doneCallout.setAttribute('tabindex', '-1');
  panel.appendChild(doneCallout);

  var saveStatus = el('p', 'ftb-note');
  panel.appendChild(saveStatus);

  // ── rendering ────────────────────────────────────────────────────────────────
  function renderStages() {
    var current = correct ? 3 : (bugFound ? 1 : 0);
    stageEls.forEach(function (li, i) {
      li.classList.remove('is-current', 'is-done');
      li.removeAttribute('aria-current');
      if (correct || i < current) { li.classList.add('is-done'); }
      else if (i === current) { li.classList.add('is-current'); li.setAttribute('aria-current', 'step'); }
    });
    lockNote.textContent = bugFound
      ? 'The code is unlocked. Fix the bug in the editor, then run your tests again.'
      : 'The code is locked until your tests find the bug.';
  }

  function resultPill(result, badCell) {
    var pill = el('span', 'ftb-result' + (result ? ' is-' + result : ''));
    if (RESULT_ICONS[result]) { pill.appendChild(svg(doc, RESULT_ICONS[result], 14)); }
    var label = (result === RESULT.BAD_LITERAL && badCell === 'expected')
      ? 'Check what it should return' : resultLabel(result);
    pill.appendChild(doc.createTextNode(label));
    return pill;
  }

  function resultTitle(result, badCell) {
    if (result === RESULT.BAD_LITERAL && badCell === 'expected') {
      return 'The "Should return" value is not valid. ' + QUOTE_HINT;
    }
    switch (result) {
      case RESULT.FAIL: return 'Your prediction is right, but the program gets it wrong — this test shows the bug.';
      case RESULT.MISPREDICT: return 'The program might not be the problem here — check what the description says should happen.';
      case RESULT.TIMEOUT: return 'This test took longer than 2 seconds, so it was stopped.';
      case RESULT.BAD_LITERAL: return QUOTE_HINT;
      default: return '';
    }
  }

  function markStale() {
    if (runFresh) { runFresh = false; updateControls(); }
  }

  function renderTable() {
    tbody.textContent = '';
    if (!rows.length) {
      var tr0 = el('tr');
      var td0 = el('td', 'ftb-empty', 'No tests yet. Add a test to get started.');
      td0.colSpan = 8;
      tr0.appendChild(td0);
      tbody.appendChild(tr0);
    }
    rows.forEach(function (row, index) {
      var n = index + 1;
      var tr = el('tr');

      var numTd = el('td', 'ftb-num', String(n));
      tr.appendChild(numTd);

      // inputs
      var inTd = el('td');
      var inWrap = el('div', 'ftb-inputs');
      if (!inputCount) { inWrap.appendChild(el('span', 'ftb-note', 'No arguments')); }
      row.inputs.forEach(function (value, i) {
        var input = el('input', 'ftb-input');
        input.type = 'text';
        input.autocomplete = 'off';
        input.spellcheck = false;
        input.value = value;
        input.placeholder = inputLabel(i);
        input.setAttribute('aria-label', 'Test ' + n + ': ' + inputLabel(i));
        input.addEventListener('input', function () { row.inputs[i] = input.value; markStale(); renderCoverage(); });
        inWrap.appendChild(input);
      });
      inTd.appendChild(inWrap);
      tr.appendChild(inTd);

      // type
      var typeTd = el('td');
      var select = el('select', 'ftb-select');
      select.setAttribute('aria-label', 'Test ' + n + ': type of test');
      ROW_TYPES.forEach(function (t) {
        var opt = el('option', null, TYPE_LABELS[t]);
        opt.value = t;
        if (row.type === t) { opt.selected = true; }
        select.appendChild(opt);
      });
      select.addEventListener('change', function () { row.type = select.value; renderCoverage(); });
      typeTd.appendChild(select);
      tr.appendChild(typeTd);

      // expected (an "expect an error" row has nothing to type — ANY error counts, never a
      // specific one; see find-the-bug-grading.js's matchesExpectation for why)
      var expTd = el('td');
      var expWrap = el('div', 'ftb-expect');
      var exp = el('input', 'ftb-input');
      exp.type = 'text';
      exp.autocomplete = 'off';
      exp.spellcheck = false;
      exp.value = row.expected;
      var errNote = el('p', 'ftb-note', 'Any error counts — no need to name it.');
      function syncExpectPlaceholder() {
        exp.hidden = row.expectError;
        errNote.hidden = !row.expectError;
        if (!row.expectError) {
          exp.placeholder = compare === 'return' ? 'e.g. 10' : 'What it prints';
          exp.setAttribute('aria-label', 'Test ' + n + ': ' + expectedHeader.toLowerCase());
        }
      }
      syncExpectPlaceholder();
      exp.addEventListener('input', function () { row.expected = exp.value; markStale(); });
      expWrap.appendChild(exp);
      expWrap.appendChild(errNote);
      var errLabel = el('label', 'ftb-check');
      var errBox = el('input');
      errBox.type = 'checkbox';
      errBox.checked = row.expectError;
      errBox.addEventListener('change', function () {
        row.expectError = errBox.checked;
        row.expected = '';
        exp.value = '';
        syncExpectPlaceholder();
        markStale();
      });
      errLabel.appendChild(errBox);
      errLabel.appendChild(doc.createTextNode('Expect an error'));
      expWrap.appendChild(errLabel);
      expTd.appendChild(expWrap);
      tr.appendChild(expTd);

      // actual — ALWAYS the code under test's
      tr.appendChild(el('td', 'ftb-actual', row.actual || ''));

      // result
      var resTd = el('td');
      if (row.result) {
        var pill = resultPill(row.result, row.badCell);
        var tip = resultTitle(row.result, row.badCell);
        resTd.appendChild(pill);
        // The explanation used to be hover-title-only (and a visually-hidden span for screen
        // readers) — a sighted, non-hovering user never saw why a Mispredict/Timeout/Check-the-
        // input pill showed up. Render it as visible text too; the visible text already reaches
        // screen readers, so no separate .ftb-sr duplicate is needed. `title` stays for a quick
        // mouse-hover confirmation.
        if (tip) { pill.title = tip; resTd.appendChild(el('p', 'ftb-result-hint', tip)); }
      } else {
        resTd.appendChild(el('span', 'ftb-result', resultLabel('')));
      }
      tr.appendChild(resTd);

      // why
      var whyTd = el('td', 'ftb-why');
      var why = el('input', 'ftb-input');
      why.type = 'text';
      why.maxLength = 5000;
      why.value = row.why;
      why.placeholder = 'Optional';
      why.setAttribute('aria-label', 'Test ' + n + ': why this test?');
      why.addEventListener('input', function () { row.why = why.value; });
      whyTd.appendChild(why);
      tr.appendChild(whyTd);

      // tools
      var toolsTd = el('td');
      var tools = el('div', 'ftb-row-tools');
      var up = button('ftb-btn-icon', null);
      up.appendChild(svg(doc, '<path d="m18 15-6-6-6 6"/>'));
      up.setAttribute('aria-label', 'Move test ' + n + ' up');
      up.disabled = index === 0 || running;
      up.addEventListener('click', function () { moveRow(index, -1); });
      var down = button('ftb-btn-icon', null);
      down.appendChild(svg(doc, '<path d="m6 9 6 6 6-6"/>'));
      down.setAttribute('aria-label', 'Move test ' + n + ' down');
      down.disabled = index === rows.length - 1 || running;
      down.addEventListener('click', function () { moveRow(index, 1); });
      var del = button('ftb-btn-icon', null);
      del.appendChild(svg(doc, '<path d="M3 6h18"/><path d="M8 6V4h8v2"/><path d="M19 6l-1 14H6L5 6"/>'));
      del.setAttribute('aria-label', 'Delete test ' + n);
      del.disabled = running;
      del.addEventListener('click', function () { deleteRow(index); });
      tools.appendChild(up);
      tools.appendChild(down);
      tools.appendChild(del);
      toolsTd.appendChild(tools);
      tr.appendChild(toolsTd);

      tbody.appendChild(tr);
    });
  }

  function focusRowInput(index) {
    var tr = tbody.querySelectorAll('tr')[index];
    var input = tr && tr.querySelector('input');
    if (input) { input.focus(); } else { addBtn.focus(); }
  }

  function moveRow(index, delta) {
    var to = index + delta;
    if (to < 0 || to >= rows.length || running) { return; }
    var tmp = rows[index]; rows[index] = rows[to]; rows[to] = tmp;
    renderTable();
    var tr = tbody.querySelectorAll('tr')[to];
    var again = tr && tr.querySelector('button[aria-label^="Move test ' + (to + 1) + (delta < 0 ? ' up' : ' down') + '"]');
    if (again && !again.disabled) { again.focus(); } else { focusRowInput(to); }
    announce('Test moved to position ' + (to + 1) + '.');
  }

  function deleteRow(index) {
    if (running) { return; }
    rows.splice(index, 1);
    markStale();
    renderTable();
    renderCoverage();
    updateControls();
    var trs = tbody.querySelectorAll('tr');
    var next = trs[Math.min(index, trs.length - 1)];
    var input = next && next.querySelector('input');
    if (rows.length && input) { input.focus(); } else { addBtn.focus(); }
    announce('Test deleted.');
  }

  function renderCoverage() {
    var cov = coverage(rows, cfg);
    coverList.textContent = '';
    coverList.appendChild(el('li', null,
      cov.counts.expected + ' expected · ' + cov.counts.boundary + ' boundary · ' + cov.counts.invalid + ' invalid'));
    cov.shortfalls.forEach(function (s) {
      var more = s.want - s.have;
      coverList.appendChild(el('li', null,
        'Try adding ' + more + ' more ' + TYPE_LABELS[s.type].toLowerCase() + ' test' + (more === 1 ? '' : 's') + '.'));
    });
    cov.offBoundaryRows.forEach(function (i) {
      coverList.appendChild(el('li', null,
        'Test ' + (i + 1) + ' is marked Boundary, but its input is not right on an edge yet.'));
    });
  }

  function renderFound() {
    foundCallout.textContent = '';
    foundCallout.hidden = !bugFound || correct;
    if (!bugFound || correct) { return; }
    var p = el('p');
    var strong = el('strong', null, 'You found the bug. ');
    p.appendChild(strong);
    p.appendChild(doc.createTextNode('At least one of your tests shows the program doing the wrong thing. The code is now unlocked: fix it, then run your tests again until every test passes.'));
    foundCallout.appendChild(p);
  }

  function renderDone() {
    doneCallout.textContent = '';
    doneCallout.hidden = !correct;
    if (!correct) { return; }
    var p = el('p');
    p.appendChild(el('strong', null, 'Bug fixed and explained. '));
    p.appendChild(doc.createTextNode('Nice work — your tests found it, and they prove your fix works.'));
    doneCallout.appendChild(p);
    if (explainModel) {
      doneCallout.appendChild(el('p', 'ftb-label', 'Compare with your teacher\'s explanation'));
      var modelText = el('p', 'ftb-model');
      modelText.innerHTML = inlineMarkdown(explainModel);
      doneCallout.appendChild(modelText);
    }
  }

  function updateControls() {
    var rowsOk = rows.length > 0;
    addBtn.disabled = running || rows.length >= maxRows;
    addBtn.title = rows.length >= maxRows ? 'This activity allows up to ' + maxRows + ' tests.' : '';
    runBtn.disabled = running || !rowsOk;
    runBtn.title = rowsOk ? '' : 'Add a test first.';
    tryBtn.disabled = running;
    explainSection.hidden = !bugFound;
    var submittable = canSubmitExplanation({
      bugFound: bugFound, allPass: allPass, runFresh: runFresh, explanation: explainBox.value
    });
    explainBtn.disabled = submitting || running || !submittable;
    if (!bugFound) {
      explainHint.textContent = '';
    } else if (!(allPass && runFresh)) {
      explainHint.textContent = 'Run your tests again after your fix. You can submit once every test passes.';
    } else if (!explainBox.value.trim()) {
      explainHint.textContent = 'Every test passes. Write your explanation to finish.';
    } else {
      explainHint.textContent = correct ? 'You can update your explanation and submit it again.' : 'Every test passes. Submit when you are ready.';
    }
    renderStages();
  }

  var announcer = el('span', 'ftb-sr');
  announcer.setAttribute('aria-live', 'polite');
  panel.appendChild(announcer);
  function announce(text) {
    announcer.textContent = '';
    announcer.textContent = text;
  }

  function setError(node, text) {
    node.textContent = text || '';
    node.hidden = !text;
  }

  // ── the editor lock ──────────────────────────────────────────────────────────
  var editor = core.getEditor();
  function applyLock() {
    if (editor && editor.setReadOnly) { editor.setReadOnly(!bugFound); }
  }

  // ── autosave-to-Draft once unlocked (the guided-make / assignment path) ─────────
  var draftId = doc.body.getAttribute('data-draft-project-id')
             || doc.body.getAttribute('data-project-id') || '';
  var saveTimer = null;
  // one-tab-same-tab-launches: returns the tracked save promise, or null when nothing saves.
  function saveRequest() {
    saveStatus.textContent = 'Saving…';
    return trackSave(api.saveDraft(draftId, core.getFiles()));
  }
  function saveNow() {
    saveTimer = null;
    saveState.pending = false;
    if (!bugFound || !draftId) { return; }
    saveRequest()
      .then(function () { saveStatus.textContent = 'Your code is saved.'; })
      .catch(function (err) { saveStatus.textContent = 'We couldn’t save your code. Stay on this page and try again.'; console.error(err); });
  }
  if (editor && editor.on) {
    editor.on('change', function () {
      if (!bugFound || (core.isLoading && core.isLoading())) { return; }
      markStale();
      if (saveTimer) { clearTimeout(saveTimer); }
      saveStatus.textContent = 'Editing…';
      saveState.pending = true;
      saveTimer = setTimeout(saveNow, AUTOSAVE_DEBOUNCE_MS);
    });
  }
  registerSaveHandler(function () {
    if (saveTimer) { clearTimeout(saveTimer); saveTimer = null; }
    saveNow();
  });
  // one-tab-same-tab-launches: the save state is live from here.
  saveState.wired = true;

  // one-tab-same-tab-launches: the exported flush (see `flushPendingSave` above).
  activeFlush = function () {
    if (saveTimer) { clearTimeout(saveTimer); saveTimer = null; }
    saveState.pending = false;
    if (!bugFound || !draftId) { return Promise.resolve(null); }
    return saveRequest()
      .then(function (res) {
        var ok = !!(res && res.success === true);
        saveStatus.textContent = ok ? 'Your code is saved.' : 'We couldn’t save your code. Stay on this page and try again.';
        return res;
      }, function (err) {
        saveStatus.textContent = 'We couldn’t save your code. Stay on this page and try again.';
        throw err;
      });
  };

  // ── the hidden reference runner (built once, kept warm) ─────────────────────
  function ensureHidden() {
    if (hidden) { return hidden.ready(); }
    hidden = createHiddenRunner({ doc: doc, ace: ace, mode: 'python', source: cfg.reference || '' });
    return hidden.ready().catch(function (err) {
      hiddenFailed = true;
      throw err;
    });
  }

  function ensureVisible() {
    if (visibleReady) { return Promise.resolve(); }
    return waitForReady(core);
  }

  // ── the visible runner's own controls, fenced off during a pass ──────────────────
  // While Run tests (or Run once) is driving the visible runner, its toolbar Run/Stop (and the
  // pre-run stage button) must not re-stage the buffer or post a conflicting runner:run
  // mid-sequence. editor-core re-enables its buttons on every runner state change (setButtons
  // runs BEFORE it emits 'state'/'ready'), so the disable is re-applied from those events, and
  // a capture-phase guard refuses any click that still gets through.
  var runnerControlIds = ['runner-run', 'runner-stop', 'stage-prerun-btn'];
  function fenceRunnerControls() {
    if (!running) { return; }
    runnerControlIds.forEach(function (id) {
      var b = doc.getElementById(id);
      if (b) { b.disabled = true; }
    });
  }
  function releaseRunnerControls() {
    var r = doc.getElementById('runner-run');
    var s = doc.getElementById('runner-stop');
    // Hand back the state editor-core would have set for an idle, ready runner.
    if (r) { r.disabled = !visibleReady; }
    if (s) { s.disabled = true; }
    var p = doc.getElementById('stage-prerun-btn');
    if (p && r) { p.disabled = r.disabled || r.getClientRects().length === 0; }
  }
  core.on('state', fenceRunnerControls);
  core.on('ready', fenceRunnerControls);
  doc.addEventListener('click', function (e) {
    if (!running) { return; }
    var t = e.target && e.target.closest ? e.target.closest('#runner-run, #runner-stop, #stage-prerun-btn') : null;
    if (t) { e.preventDefault(); e.stopPropagation(); }
  }, true);

  // Parse every literal in one run. Returns an array aligned with `raw`, or null.
  function parseAll(raw) {
    if (!raw.length) { return Promise.resolve([]); }
    return hidden.runSource(buildParseProgram(raw), [], PARSE_TIMEOUT_MS).then(function (res) {
      if (!res || res.status !== 'ok' || !res.value) { return null; }
      return readParseOutput(res.value.stdout, raw.length);
    });
  }

  function observe(res) {
    if (!res || res.status !== 'ok') { return { kind: 'timeout' }; }
    if (target === 'stdin') { return observeConsoleRun(res.value); }
    var results = (res.value && res.value.results) || [];
    return observeFunctionResult(results[0]);
  }

  function runCodeUnderTest(values, strs) {
    if (target === 'stdin') {
      return runWithTimeout(core, function () { return core.requestConsoleRun(strs); }, DEFAULT_CALL_TIMEOUT_MS);
    }
    return runWithTimeout(core, function () {
      return core.requestFunctionCall([{ functionName: cfg.functionName, args: values }]);
    }, DEFAULT_CALL_TIMEOUT_MS);
  }

  function runReference(values, strs) {
    if (target === 'stdin') { return hidden.consoleRun(strs, DEFAULT_CALL_TIMEOUT_MS); }
    return hidden.callFunction(cfg.functionName, values, DEFAULT_CALL_TIMEOUT_MS);
  }

  function beginBusy(text) {
    running = true;
    fenceRunnerControls();
    status.textContent = text;
    setError(errorEl, '');
    renderTable();
    updateControls();
  }
  function endBusy() {
    running = false;
    releaseRunnerControls();
    renderTable();
    updateControls();
  }

  // ── Run tests ────────────────────────────────────────────────────────────────
  runBtn.addEventListener('click', function () {
    if (running || !rows.length) { return; }
    beginBusy('Getting the checker ready…');
    Promise.all([ensureVisible(), ensureHidden()]).then(function () {
      // Every raw literal: each row's inputs, then (return compare, not an error row) its
      // expected value.
      var raw = [];
      var map = rows.map(function (row) {
        var entry = { inputs: [], expected: -1 };
        row.inputs.forEach(function (v) { entry.inputs.push(raw.length); raw.push(v); });
        if (compare === 'return' && !row.expectError) { entry.expected = raw.length; raw.push(row.expected); }
        return entry;
      });
      status.textContent = 'Reading your inputs…';
      return parseAll(raw).then(function (parsed) {
        if (!parsed) {
          throw new Error('parse-failed');
        }
        var results = [];
        var chain = Promise.resolve();
        rows.forEach(function (row, i) {
          chain = chain.then(function () {
            status.textContent = 'Running test ' + (i + 1) + ' of ' + rows.length + '…';
            var m = map[i];
            var ins = m.inputs.map(function (k) { return parsed[k]; });
            var expParsed = m.expected >= 0 ? parsed[m.expected] : null;
            var badLiteral = ins.some(function (p) { return !p || !p.ok || (target === 'function' && !p.hasValue); })
              || (expParsed && (!expParsed.ok || !expParsed.hasValue));
            if (badLiteral) {
              var inputsBad = ins.some(function (p) { return !p || !p.ok || (target === 'function' && !p.hasValue); });
              row.actual = '';
              row.badCell = inputsBad ? 'input' : 'expected';
              row.result = RESULT.BAD_LITERAL;
              results.push(row.result);
              return null;
            }
            var values = ins.map(function (p) { return p.value; });
            var strs = ins.map(function (p) { return p.str; });
            var expectation = {
              expectError: row.expectError,
              value: expParsed ? expParsed.value : undefined,
              valueIsFloat: !!(expParsed && expParsed.pyType === 'float'),
              text: row.expected
            };
            return runCodeUnderTest(values, strs).then(function (cutRes) {
              var cut = observe(cutRes);
              return runReference(values, strs).then(function (refRes) {
                var ref = observe(refRes);
                row.actual = formatObserved(cut, compare);
                row.result = gradeRow({ cut: cut, ref: ref, expectation: expectation, compare: compare });
                results.push(row.result);
              });
            });
          });
        });
        return chain.then(function () { return results; });
      });
    }).then(function (results) {
      var summary = summarise(results);
      var wasFound = bugFound;
      bugFound = stickyBugFound(bugFound, summary.bugFound);
      allPass = summary.allPass;
      runFresh = true;
      var passCount = results.filter(function (r) { return r === RESULT.PASS; }).length;
      status.textContent = 'Done: ' + passCount + ' of ' + results.length + ' tests passed.';
      if (bugFound && !wasFound) {
        applyLock();
        announce('You found the bug. The code is now unlocked so you can fix it.');
      } else if (bugFound && allPass) {
        announce('Every test passes. Write your explanation to finish.');
      }
      renderFound();
      endBusy();
      return postResponse('run');
    }).catch(function (err) {
      console.error(err);
      endBusy();
      status.textContent = '';
      if (hiddenFailed) {
        setError(errorEl, 'The checker could not start, so your tests could not be marked. Reload the page to try again.');
      } else if (err && err.message === 'parse-failed') {
        setError(errorEl, 'Your inputs could not be read this time. Run your tests again.');
      } else {
        setError(errorEl, 'Something went wrong while running your tests. Run them again.');
      }
    });
  });

  // ── Try one input ────────────────────────────────────────────────────────────
  tryBtn.addEventListener('click', function () {
    if (running) { return; }
    running = true;
    fenceRunnerControls();
    updateControls();
    tryOut.hidden = false;
    tryOut.textContent = 'Running…';
    var raw = tryInputs.map(function (i) { return i.value; });
    Promise.all([ensureVisible(), ensureHidden()]).then(function () { return parseAll(raw); }).then(function (parsed) {
      if (!parsed) { tryOut.textContent = 'That input could not be read. Try again.'; return null; }
      var badAt = parsed.findIndex(function (p) { return !p.ok || (target === 'function' && !p.hasValue); });
      if (badAt !== -1) { tryOut.textContent = inputLabel(badAt) + ': ' + QUOTE_HINT; return null; }
      var values = parsed.map(function (p) { return p.value; });
      var strs = parsed.map(function (p) { return p.str; });
      return runCodeUnderTest(values, strs).then(function (res) {
        var obs = observe(res);
        var lines = [];
        if (obs.kind === 'timeout') { lines.push('It did not finish within 2 seconds.'); }
        else if (obs.kind === 'missing') { lines.push('The program has no function called ' + (cfg.functionName || '') + '.'); }
        else {
          if (obs.stdout) { lines.push('Printed:\n' + obs.stdout.replace(/\s+$/, '')); }
          if (obs.kind === 'error') { lines.push('Raised: ' + obs.type); }
          else if (target === 'function') { lines.push('Returned: ' + formatObserved(obs, 'return')); }
          if (!lines.length) { lines.push('(It printed nothing.)'); }
        }
        tryOut.textContent = lines.join('\n\n');
      });
    }).catch(function (err) {
      console.error(err);
      tryOut.textContent = hiddenFailed
        ? 'The checker could not start. Reload the page to try again.'
        : 'Something went wrong. Try again.';
    }).then(function () {
      running = false;
      releaseRunnerControls();
      updateControls();
    });
  });

  // ── Add a test ───────────────────────────────────────────────────────────────
  addBtn.addEventListener('click', function () {
    if (running || rows.length >= maxRows) { return; }
    rows.push(blankRow());
    markStale();
    renderTable();
    renderCoverage();
    updateControls();
    var trs = tbody.querySelectorAll('tr');
    var input = trs[trs.length - 1] && trs[trs.length - 1].querySelector('input, select');
    if (input) { input.focus(); }
    announce('Test ' + rows.length + ' added.');
  });

  explainBox.addEventListener('input', function () { explanation = explainBox.value; updateControls(); });

  // ── persistence ─────────────────────────────────────────────────────────────
  function payload(action) {
    return {
      action: action,
      rows: rows.map(function (r) {
        return {
          inputs: r.inputs.slice(0, 10).map(function (v) { return String(v).slice(0, 5000); }),
          type: r.type,
          expectError: !!r.expectError,
          expected: String(r.expected).slice(0, 5000),
          actual: String(r.actual || '').slice(0, 5000),
          result: r.result || '',
          why: String(r.why || '').slice(0, 5000)
        };
      }),
      bugFound: bugFound,
      allPass: allPass && runFresh,
      explanation: String(explainBox.value || '').slice(0, 5000),
      correct: action === 'explain'
    };
  }

  function respondUrl() {
    return '/api/courses/' + cfg.courseId + '/lessons/' + cfg.lessonId + '/materials/' + cfg.materialId
      + (previewMode ? '/preview-respond' : '/respond');
  }

  function post(action) {
    var body = { answers: payload(action) };
    if (cfg.linkedCourseId && !previewMode) { body.linkedCourseId = cfg.linkedCourseId; }
    return fetch(respondUrl(), {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    }).then(function (res) {
      if (!res || !res.ok) { throw new Error('respond-' + (res && res.status)); }
      return res.json().catch(function () { return {}; });
    });
  }

  function postResponse(action) {
    // A teacher (not previewing) is refused by the server's student-only write gate — expected
    // and silent, exactly as on the debug surface.
    if (canManage && !previewMode) { return Promise.resolve(); }
    return post(action).then(function () {
      if (!previewMode) { saveStatus.textContent = 'Your test results are saved.'; }
    }).catch(function (err) {
      console.error(err);
      setError(errorEl, 'Your test results could not be saved. Check your connection, then run your tests again.');
    });
  }

  explainBtn.addEventListener('click', function () {
    if (explainBtn.disabled) { return; }
    submitting = true;
    updateControls();
    setError(explainError, '');
    explainBtn.textContent = 'Sending…';
    var send = (canManage && !previewMode) ? Promise.resolve({}) : post('explain');
    send.then(function (json) {
      var data = (json && json.data) ? json.data : (json || {});
      var stored = data.answers || {};
      var passed = previewMode || (canManage && !previewMode) || stored.correct === true;
      if (!passed) {
        setError(explainError, 'Your explanation was not accepted. Run your tests again, then submit.');
        return;
      }
      correct = true;
      if (typeof data.explainModel === 'string') { explainModel = data.explainModel; }
      renderFound();
      renderDone();
      announce('Your explanation is in. The activity is complete.');
      doneCallout.focus();
      if (!previewMode && !canManage) { offerClose({ sameTab: true, label: 'Nice work — you found and fixed the bug.' }); }
    }).catch(function (err) {
      console.error(err);
      setError(explainError, 'Your explanation could not be sent. Check your connection and try again.');
      if (!previewMode && !canManage) { offerReturn({ sameTab: true, warn: true, label: 'We couldn’t save that. Please tell your teacher.' }); }
    }).then(function () {
      submitting = false;
      explainBtn.textContent = 'Submit my explanation';
      updateControls();
    });
  });

  // ── first render ────────────────────────────────────────────────────────────
  applyLock();
  renderTable();
  renderCoverage();
  renderFound();
  renderDone();
  updateControls();
  if (resumed && resumed.rows && resumed.rows.length && !correct) {
    status.textContent = 'These are your results from last time. Run your tests again to update them.';
  }

  // Warm the hidden runner once the visible one is up, so the two Pyodide loads never compete.
  core.on('ready', function () {
    if (!hidden) { ensureHidden().catch(function () { /* reported on the next Run tests */ }); }
  });
}
